import { useEffect, useRef, type KeyboardEvent, type ReactNode } from 'react';

import { cn } from '../../lib/utils';

/**
 * WizardPanel — reusable accordion panel for multi-panel wizard UIs (ADR-103
 * "Multi-Panel Order Builder" pattern). Extracted from rental wizard in E-105
 * Story S1 so that rental, worksheet (E-105 Phase 2), service quote, goods
 * receipt and other wizard flows can share one accessible implementation.
 *
 * A11y:
 * - aria-expanded / aria-controls on the header button
 * - role="region" + aria-labelledby on panel content
 * - hidden attribute (not just CSS display:none) — screen reader compliant
 * - Enter / Space toggle via default button semantics
 * - Escape (panel-content scope) closes the panel and refocuses header
 * - Focus management: on open, first focusable inside panel gets focus; on
 *   close, focus returns to the header
 * - prefers-reduced-motion: animation becomes instant (Tailwind motion-safe:)
 */

export interface WizardPanelProps {
  /** Stable panel key within its wizard (e.g. 'partner', 'equipment'). */
  id: string;
  /**
   * DOM id prefix to isolate panel ids across concurrent wizards. Generated
   * DOM ids: `${namespace}-${id}` (content) and `${namespace}-${id}-header`
   * (header button). Default: `'wizard-panel'`.
   */
  namespace?: string;
  /** Visible title shown in the header. */
  title: string;
  /** Optional snippet rendered under the title while the panel is collapsed. */
  summary?: ReactNode;
  /** Whether the panel is currently expanded. */
  isOpen: boolean;
  /** Whether the panel content is valid (renders ✓ badge when collapsed). */
  isValid: boolean;
  /** Called when the user activates the header or presses Escape in an open panel. */
  onToggle: () => void;
  /** Panel body. Only rendered while the panel is open. */
  children: ReactNode;
  /** Optional step number shown in the header badge when not yet valid. */
  stepNumber?: number;
  /**
   * Single-page mode (ADR-103 Amendment-3): the panel is always expanded and
   * renders as a static section — no toggle button, chevron, aria-expanded or
   * collapse. `isOpen`/`onToggle` are ignored. Used by the rental wizard V2
   * 3-col re-layout where the accordion interaction-model is replaced.
   * Default false → unchanged accordion behavior (backward-compatible).
   */
  nonCollapsible?: boolean;
  /**
   * Heading element used for the section title in `nonCollapsible` mode, so the
   * panel title fits the host page's heading hierarchy (a panel container title
   * should outrank its inner step-content headings). Default `'h3'` keeps the
   * existing markup for current consumers; the rental wizard V2 passes `'h2'`
   * (page `<h1>` → panel `<h2>` → step `<h3>`) to avoid an h1→h3 skip + inversion.
   * Visual size is unchanged (className stays the same) — semantics only.
   */
  headingLevel?: 'h2' | 'h3';
}

export function WizardPanel({
  id,
  namespace = 'wizard-panel',
  title,
  summary,
  isOpen,
  isValid,
  onToggle,
  children,
  stepNumber,
  nonCollapsible = false,
  headingLevel = 'h3',
}: WizardPanelProps) {
  const HeadingTag = headingLevel;
  const panelId = `${namespace}-${id}`;
  const headerId = `${namespace}-${id}-header`;
  const headerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const previousIsOpen = useRef(isOpen);

  // Focus management: on open, focus the first focusable inside the panel;
  // on close, return focus to the header.
  useEffect(() => {
    if (isOpen && !previousIsOpen.current) {
      const firstFocusable = panelRef.current?.querySelector<HTMLElement>(
        'input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
      if (firstFocusable) {
        requestAnimationFrame(() => firstFocusable.focus());
      }
    } else if (!isOpen && previousIsOpen.current) {
      headerRef.current?.focus();
    }
    previousIsOpen.current = isOpen;
  }, [isOpen]);

  // Escape inside an open panel → close and let the effect refocus the header.
  const handlePanelKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape' && isOpen) {
      e.stopPropagation();
      onToggle();
    }
  };

  // ADR-103 Amendment-3 single-page mode: always-expanded static section,
  // no toggle button / chevron / aria-expanded. isOpen/onToggle ignored.
  if (nonCollapsible) {
    return (
      <div data-wizard-panel-id={id} className="border rounded-lg bg-card border-border">
        <div id={headerId} className="flex w-full items-center gap-3 px-5 py-4 text-left">
          <div
            className={cn(
              'flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold',
              isValid ? 'bg-green-500 text-white' : 'bg-primary text-primary-foreground'
            )}
            aria-hidden="true"
          >
            {isValid ? '✓' : (stepNumber ?? '')}
          </div>
          {/* Am-3 Inc-3 a11y: heading-elem → a screen-reader szekciónként navigálhat
              (a static-div header nem volt tab/heading-elérhető). A heading-szint
              `headingLevel` prop-ból (default h3; rental V2 h2 → h1→h2→h3 folytonos). */}
          <HeadingTag className="text-sm font-semibold text-foreground">{title}</HeadingTag>
        </div>
        <div
          id={panelId}
          role="region"
          aria-labelledby={headerId}
          className="border-t border-border px-5 pb-5 pt-4"
        >
          {children}
        </div>
      </div>
    );
  }

  return (
    <div
      data-wizard-panel-id={id}
      className={cn(
        'border rounded-lg bg-card transition-shadow',
        isOpen ? 'border-primary shadow-md' : 'border-border hover:shadow-sm'
      )}
    >
      {/* a11y: WAI-ARIA accordion-pattern — a heading wrap-eli a toggle-button-t
          (heading-szint `headingLevel` prop-ból, default h3). Így a collapsible
          V1-flow heading-hierarchiája is konzisztens (page h1 → panel h2 → step h3),
          nincs h1→h3 skip. A heading reset-styling (m-0) → a layout változatlan. */}
      <HeadingTag className="m-0">
        <button
          ref={headerRef}
          id={headerId}
          data-wizard-panel-header
          type="button"
          onClick={onToggle}
          aria-expanded={isOpen}
          aria-controls={panelId}
          className="w-full px-5 py-4 flex items-center justify-between text-left hover:bg-muted/50 motion-safe:transition"
        >
          <div className="flex items-center gap-3">
            <div
              className={cn(
                'w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold',
                isValid
                  ? 'bg-green-500 text-white'
                  : isOpen
                    ? 'bg-primary text-primary-foreground ring-2 ring-primary/30'
                    : 'bg-muted text-muted-foreground'
              )}
              aria-hidden="true"
            >
              {isValid ? '✓' : (stepNumber ?? '')}
            </div>
            <div>
              <span className="font-semibold text-sm text-foreground">{title}</span>
              {!isOpen && summary && (
                <div className="text-xs text-muted-foreground mt-0.5">{summary}</div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <svg
              className={cn(
                'h-4 w-4 text-muted-foreground motion-safe:transition-transform',
                isOpen && 'rotate-180'
              )}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
        </button>
      </HeadingTag>
      <div
        ref={panelRef}
        id={panelId}
        role="region"
        aria-labelledby={headerId}
        hidden={!isOpen}
        onKeyDown={handlePanelKeyDown}
        className={cn('border-t border-border', isOpen ? 'px-5 pb-5 pt-4' : '')}
      >
        {isOpen && children}
      </div>
    </div>
  );
}

/**
 * Keyboard navigation helper for grouped WizardPanel headers. Add this handler
 * to a parent container that wraps multiple WizardPanels. Handles:
 * - ArrowDown / ArrowUp: move focus to next / previous header (wraps)
 * - Home / End: focus the first / last header
 */
export function handleWizardPanelKeyNav(e: KeyboardEvent<HTMLElement>): void {
  const target = e.target as HTMLElement;
  if (!target?.matches?.('[data-wizard-panel-header]')) return;

  const container = e.currentTarget as HTMLElement;
  const headers = Array.from(
    container.querySelectorAll<HTMLButtonElement>('[data-wizard-panel-header]')
  );
  const currentIndex = headers.indexOf(target as HTMLButtonElement);
  if (currentIndex === -1) return;

  let nextIndex: number | null = null;
  switch (e.key) {
    case 'ArrowDown':
      nextIndex = (currentIndex + 1) % headers.length;
      break;
    case 'ArrowUp':
      nextIndex = (currentIndex - 1 + headers.length) % headers.length;
      break;
    case 'Home':
      nextIndex = 0;
      break;
    case 'End':
      nextIndex = headers.length - 1;
      break;
  }

  if (nextIndex !== null) {
    e.preventDefault();
    headers[nextIndex]?.focus();
  }
}
